import React, { useState } from "react";
import {
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
    FormControlLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Stepper,
    Step,
    StepLabel,
    Link,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AuthService from "../services/Auth.service";
import kodexLogo from "../assets/kodex_logo.svg";

const RECOVERY_STEPS = ["Send Recovery Email", "Reset Password"];

function ForgotDialog({ open, onClose }) {
    const [activeStep, setActiveStep] = useState(0);

    const [email, setEmail] = useState("");
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailMsg, setEmailMsg] = useState(null);

    const [uid, setUid] = useState("");
    const [token, setToken] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [resetMsg, setResetMsg] = useState(null);

    const handleClose = () => {
        onClose();
        setTimeout(() => {
            setActiveStep(0);
            setEmail("");
            setEmailMsg(null);
            setUid("");
            setToken("");
            setNewPassword("");
            setConfirmPassword("");
            setResetMsg(null);
        }, 300);
    };

    const handleSendEmail = async (e) => {
        e.preventDefault();
        setEmailMsg(null);
        setEmailLoading(true);
        try {
            const res = await AuthService.forgotPassword(email);
            setEmailMsg({ type: "success", text: res.data.detail });
        } catch (err) {
            setEmailMsg({
                type: "error",
                text: err?.response?.data?.detail || "Failed to send recovery email.",
            });
        } finally {
            setEmailLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setResetMsg({ type: "error", text: "Passwords do not match." });
            return;
        }
        setResetMsg(null);
        setResetLoading(true);
        try {
            const res = await AuthService.resetPassword(uid, token, newPassword);
            setResetMsg({ type: "success", text: res.data.detail });
        } catch (err) {
            setResetMsg({
                type: "error",
                text:
                    err?.response?.data?.detail ||
                    "Password reset failed. Check your codes and try again.",
            });
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                elevation: 0,
                sx: { border: "1px solid", borderColor: "primary.main", borderRadius: 2 },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>Account Recovery</DialogTitle>
            <DialogContent>
                <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                    {RECOVERY_STEPS.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {activeStep === 0 && (
                    <Box component="form" onSubmit={handleSendEmail}>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Enter your email address. We'll send you your username and a
                            password reset code.
                        </Typography>
                        {emailMsg && (
                            <Alert severity={emailMsg.type} sx={{ mb: 2 }}>
                                {emailMsg.text}
                            </Alert>
                        )}
                        <TextField
                            label="Email Address"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                            required
                            autoFocus
                            size="small"
                            sx={{ mb: 2 }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disableElevation
                            disabled={emailLoading}
                            startIcon={emailLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {emailLoading ? "Sending…" : "Send Recovery Email"}
                        </Button>
                    </Box>
                )}

                {activeStep === 1 && (
                    <Box component="form" onSubmit={handleResetPassword}>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Enter the UID and Token from your recovery email, then choose a
                            new password.
                        </Typography>
                        {resetMsg && (
                            <Alert severity={resetMsg.type} sx={{ mb: 2 }}>
                                {resetMsg.text}
                            </Alert>
                        )}
                        <TextField
                            label="UID"
                            value={uid}
                            onChange={(e) => setUid(e.target.value)}
                            fullWidth
                            required
                            size="small"
                            sx={{ mb: 1.5 }}
                        />
                        <TextField
                            label="Token"
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                            fullWidth
                            required
                            size="small"
                            sx={{ mb: 1.5 }}
                        />
                        <Divider sx={{ my: 1.5 }} />
                        <TextField
                            label="New Password"
                            type={showNew ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            fullWidth
                            required
                            size="small"
                            sx={{ mb: 1.5 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" edge="end" onClick={() => setShowNew((v) => !v)}>
                                            {showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            label="Confirm New Password"
                            type={showConfirm ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            fullWidth
                            required
                            size="small"
                            sx={{ mb: 2 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" edge="end" onClick={() => setShowConfirm((v) => !v)}>
                                            {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disableElevation
                            disabled={resetLoading || resetMsg?.type === "success"}
                            startIcon={resetLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {resetLoading ? "Resetting…" : "Reset Password"}
                        </Button>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
                <Button size="small" onClick={handleClose}>
                    Close
                </Button>
                <Box sx={{ display: "flex", gap: 1 }}>
                    {activeStep === 1 && (
                        <Button size="small" onClick={() => setActiveStep(0)}>
                            Back
                        </Button>
                    )}
                    {activeStep === 0 && (
                        <Button size="small" onClick={() => setActiveStep(1)}>
                            Already have a code?
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
}

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from
        ? `${location.state.from.pathname}${location.state.from.search || ""}`
        : "/";
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [stayLoggedIn, setStayLoggedIn] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [forgotOpen, setForgotOpen] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(username, password, stayLoggedIn);
            navigate(from, { replace: true });
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "Login failed. Check your credentials.";
            setError(detail);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{
                bgcolor: "background.default",
                height: "100dvh",
                overflow: "hidden",
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    width: "100%",
                    maxWidth: 380,
                    border: "1px solid",
                    borderColor: "primary.main",
                    borderRadius: 2,
                    mx: 2,
                }}
            >
                <Box textAlign="center" mb={3}>
                    <Box
                        component="img"
                        src={kodexLogo}
                        alt="Kodex Shielding Solutions"
                        sx={{ height: 80, width: "auto", mb: 1.5 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                        Sign in to continue
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                        label="Username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        fullWidth
                        required
                        autoFocus
                        autoComplete="username"
                        size="small"
                        sx={{ mb: 2 }}
                        inputProps={{ autoCapitalize: "none", autoCorrect: "off", spellCheck: false }}
                    />
                    <TextField
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        fullWidth
                        required
                        autoComplete="current-password"
                        size="small"
                        sx={{ mb: 3 }}
                        inputProps={{ autoCapitalize: "none", autoCorrect: "off", spellCheck: false }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => setShowPassword((v) => !v)}
                                        edge="end"
                                    >
                                        {showPassword ? (
                                            <VisibilityOff fontSize="small" />
                                        ) : (
                                            <Visibility fontSize="small" />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={stayLoggedIn}
                                onChange={(e) => setStayLoggedIn(e.target.checked)}
                            />
                        }
                        label={
                            <Typography variant="body2" color="text.secondary">
                                Stay logged in for 30 days
                            </Typography>
                        }
                        sx={{ mb: 2, ml: 0 }}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disableElevation
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {loading ? "Signing in…" : "Sign In"}
                    </Button>
                    <Box textAlign="center" mt={2}>
                        <Button
                            size="small"
                            variant="text"
                            sx={{ textTransform: "none", color: "text.secondary" }}
                            onClick={() => setForgotOpen(true)}
                        >
                            Forgot password or username?
                        </Button>
                    </Box>
                </Box>
            </Paper>
            <Box textAlign="center" mt={2}>
                <Typography variant="caption" color="text.secondary">
                    <Link component={RouterLink} to="/terms" color="inherit">
                        Terms of Service
                    </Link>
                    {" · "}
                    <Link component={RouterLink} to="/privacy" color="inherit">
                        Privacy Policy
                    </Link>
                </Typography>
            </Box>
            <ForgotDialog open={forgotOpen} onClose={() => setForgotOpen(false)} />
        </Box>
    );
}
